import { useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Language } from '@/i18n/translations';

/**
 * Returns the locale prefix for a given language.
 * bg = "" (root), en = "/en", ru = "/ru"
 */
export const getLocalePrefix = (lang: Language): string => {
  if (lang === 'bg') return '';
  return `/${lang}`;
};

/**
 * Extracts language from a URL pathname.
 * /en/... → 'en', /ru/... → 'ru', /... → 'bg'
 */
export const getLanguageFromPath = (pathname: string): Language => {
  if (pathname.startsWith('/en/') || pathname === '/en') return 'en';
  if (pathname.startsWith('/ru/') || pathname === '/ru') return 'ru';
  return 'bg';
};

/**
 * Strips the locale prefix from a pathname.
 * /en/properties → /properties, /ru/auth → /auth, /properties → /properties
 */
export const stripLocalePrefix = (pathname: string): string => {
  if (pathname.startsWith('/en/')) return pathname.slice(3);
  if (pathname.startsWith('/ru/')) return pathname.slice(3);
  if (pathname === '/en' || pathname === '/ru') return '/';
  return pathname;
};

/**
 * Hook that returns a function to build locale-prefixed paths.
 */
export const useLocalePath = () => {
  const { language } = useLanguage();

  const localePath = useCallback(
    (path: string): string => {
      const prefix = getLocalePrefix(language);
      if (path === '/') return prefix || '/';
      return `${prefix}${path}`;
    },
    [language]
  );

  return localePath;
};
